import {
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFintechUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFintechUiComponents, SelectedStatus } from "@b2bportal/core-types";

import { useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum } from "@tenant/ui";

import clsx from "clsx";
import {
  type FintechMarketplaceProductOfferMeta,
  type FintechMarketplaceProductOfferSelection,
  type FintechMarketplaceProductProps,
  type FintechMarketplaceProductsSelection,
  type FintechMarketplaceConfig,
  FintechMarketplaceProductViewType,
} from "../../FintechMarketplaceConfig";

import { FintechMarketplaceProductBreadcrumbList } from "../FintechMarketplaceProductBreadcrumbList";
import defaultStyles from "./FintechMarketplaceSection.module.scss";

export type FintechMarketplaceSectionProps = Required<
  Pick<
    FintechMarketplaceConfig,
    | "canContinue"
    | "onContinue"
    | "productViewType"
    | "PriceBreakdown"
    | "TripDetails"
  >
> &
  Pick<FintechMarketplaceConfig, "productsBreadcrumbs"> & {
    currentProductKey: string;
    offeredProducts: ReadonlyMap<
      string,
      {
        View: ComponentType<FintechMarketplaceProductProps>;
        offers: readonly FintechMarketplaceProductOfferMeta[];
      }
    >;
    onProductsSelectionChange: (
      productsSelection: FintechMarketplaceProductsSelection
    ) => void;
    productsSelection?: Partial<
      Record<string, FintechMarketplaceProductOfferSelection>
    >;
  };

export const FintechMarketplaceSection = (
  props: FintechMarketplaceSectionProps
) => {
  const [block, cn] = useModuleBEM(
    useFintechUiStyles(
      CoreFintechUiComponents.FintechMarketplaceSection,
      defaultStyles
    ),
    CoreFintechUiComponents.FintechMarketplaceSection
  );

  const {
    PriceBreakdown,
    TripDetails,
    currentProductKey,
    onContinue: onContinueProp,
    productViewType,
  } = props;

  const {
    canContinue,
    offeredProductEntriesWithOnSelect,
    productsSelection,
    offeredProductsBreadcrumbs,
  } = useFintechMarketplaceSectionState(props);

  const { t } = useI18nContext();

  const onContinue = useCallback(() => {
    onContinueProp(productsSelection);
  }, [onContinueProp, productsSelection]);

  return (
    <div className={clsx(block, cn(currentProductKey))}>
      <div className={cn("body")}>
        <div className={cn("content")}>
          <div className={cn("content-main")}>
            {offeredProductsBreadcrumbs != null &&
              Object.keys(offeredProductsBreadcrumbs).length > 0 && (
                <div className={cn("content-breadcrumbs")}>
                  <FintechMarketplaceProductBreadcrumbList
                    productsBreadcrumbs={offeredProductsBreadcrumbs}
                    currentProductKey={currentProductKey}
                  />
                </div>
              )}
            <div
              className={cn("content-products", [
                productViewType.toLowerCase(),
              ])}
            >
              {offeredProductEntriesWithOnSelect.map(
                ({ key, View, onSelect, offers }) => (
                  <section
                    key={key}
                    className={cn("content-section", ["main"], {
                      current: key === currentProductKey,
                    })}
                  >
                    <View
                      onSelect={onSelect}
                      offers={offers}
                      productViewType={productViewType}
                    />
                  </section>
                )
              )}
            </div>
          </div>

          <div className={cn("content-sidebar")}>
            <section className={cn("content-section", ["trip-details"])}>
              <TripDetails />
            </section>
            <section className={cn("content-section", ["payment-breakdown"])}>
              <PriceBreakdown productsSelection={productsSelection} />
              {productViewType === FintechMarketplaceProductViewType.STACK && (
                <div className={cn("continue-block")}>
                  {!canContinue && (
                    <div className={cn("continue-prompt")}>
                      {t(
                        "core-fintech-ui.fintech-marketplace.selectAllToContinuePrompt"
                      )}
                    </div>
                  )}
                  <Button
                    className={cn("continue-button")}
                    onClick={onContinue}
                    buttonType={ButtonTypeEnum.Primary}
                    disabled={!canContinue}
                  >
                    {t("core-fintech-ui.fintech-marketplace.continue")}
                  </Button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export const useFintechMarketplaceSectionState = (
  props: FintechMarketplaceSectionProps
) => {
  const {
    offeredProducts,
    canContinue: canContinueFn,
    onProductsSelectionChange,
    productsBreadcrumbs,
    productsSelection: productsSelectionInit,
  } = props;

  const offeredProductEntries = useMemo(
    () => [...offeredProducts.entries()],
    [offeredProducts]
  );

  const [productsSelection, setProductsSelection] =
    useState<FintechMarketplaceProductsSelection>(() =>
      Object.fromEntries(
        offeredProductEntries.map(([key]) => [
          key,
          productsSelectionInit?.[key] ?? {
            status: SelectedStatus.UNSELECTED,
          },
        ])
      )
    );

  const offeredKeys = useMemo(
    () => new Set(offeredProductEntries.map(([key]) => key)),
    [offeredProductEntries]
  );

  // Sync productsSelection when productsSelectionInit changes
  // (e.g. Redux state updated after initial render).
  // Only sync keys for products that are actually offered — products with no
  // offers are filtered out of offeredProducts and must not leak into the
  // selection state, otherwise canContinue may never become true.
  // Exception: "headless" products (e.g. synthetic CFAR) that have no
  // marketplace offers (data: []) but carry an externally-managed SELECTED
  // status are allowed through so their selection is not silently dropped.
  useEffect(() => {
    if (productsSelectionInit == null) {
      return;
    }

    setProductsSelection((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [key, selection] of Object.entries(productsSelectionInit)) {
        if (selection == null || prev[key] === selection) {
          continue;
        }

        const isOffered = offeredKeys.has(key);
        const isHeadlessSelected = selection.status === SelectedStatus.SELECTED;

        if (isOffered || isHeadlessSelected) {
          next[key] = selection;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [productsSelectionInit, offeredKeys]);

  const offeredProductEntriesWithOnSelect = useMemo(
    () =>
      offeredProductEntries.map(([key, { View, offers }]) => ({
        key,
        View,
        offers,
        onSelect: (selected: FintechMarketplaceProductOfferSelection) => {
          // Products with init selection are managed externally — skip local
          // state update but still notify the parent via onProductsSelectionChange.
          if (productsSelectionInit?.[key] != null) {
            // Always notify parent for externally managed products,
            // even if selection hasn't changed (required for mobile Continue flow)
            onProductsSelectionChange({
              ...productsSelection,
              [key]: selected,
            });
            return;
          }

          setProductsSelection((prev) => {
            if (isSelectionEqual(prev[key], selected)) {
              return prev;
            }

            const newProductsSelection: FintechMarketplaceProductsSelection = {
              ...prev,
              [key]: selected,
            };

            onProductsSelectionChange(newProductsSelection);

            return newProductsSelection;
          });
        },
      })),
    [
      offeredProductEntries,
      setProductsSelection,
      onProductsSelectionChange,
      productsSelectionInit,
      productsSelection,
    ]
  );

  const offeredProductsBreadcrumbs = useMemo(
    () =>
      productsBreadcrumbs
        ? Object.fromEntries(
            offeredProductEntriesWithOnSelect
              .filter(({ key }) => key in productsBreadcrumbs)
              .map(({ key }) => [key, productsBreadcrumbs[key]])
          )
        : undefined,
    [offeredProductEntriesWithOnSelect, productsBreadcrumbs]
  );

  const canContinue = useMemo(
    () => canContinueFn(productsSelection),
    [productsSelection, canContinueFn]
  );

  return {
    productsSelection,
    offeredProductEntriesWithOnSelect,
    offeredProductsBreadcrumbs,
    canContinue,
  };
};

export const isSelectionEqual = (
  prev: FintechMarketplaceProductOfferSelection | undefined,
  next: FintechMarketplaceProductOfferSelection
): boolean => {
  if (prev == null) {
    return false;
  }

  if (prev.status !== next.status) {
    return false;
  }

  if (
    prev.status === SelectedStatus.SELECTED &&
    next.status === SelectedStatus.SELECTED
  ) {
    return prev.offer.payload === next.offer.payload;
  }

  return true;
};
